import { createSelector } from 'reselect';
import { Asset, Depot, DoorStatus, Thing } from '../api/types';
import Permission from '../common/permissions';
import { RootState } from '../store/store';
import { makePermissionListSelector } from './authenticationSelector';

const stateKey = 'depots';

export const depotListSelector = (state: RootState): Depot[] =>
    state.legacy[stateKey].depots;
export const doorStatusListSelector = (
    state: RootState,
): Record<string, DoorStatus> => state.legacy[stateKey].doorStatus;
export const doorStatusLoadingListSelector = (
    state: RootState,
): Record<string, boolean> => state.legacy[stateKey].doorStatusLoading;
export const thingListSelector = (state: RootState): Thing[] =>
    state.legacy[stateKey].things;
export const assetListSelector = (state: RootState): Asset[] =>
    state.legacy[stateKey].assets;

export const makeDepotSelector = (depotId: string) => {
    return createSelector(
        [depotListSelector],
        (depotsList): Depot | undefined => {
            return depotsList.find((depot) => depot.id === depotId);
        },
    );
};

// TODO: Allow access to all depots if user has access to the according location
export const makePermittedDepotListSelector = () => {
    return createSelector(
        [depotListSelector, makePermissionListSelector()],
        (depotsList, permissionsList): Depot[] => {
            return depotsList.filter((depot) =>
                permissionsList.some(
                    /* Either full permission to access the given station or for at least one door of this station */
                    (permission) => {
                        const hasFullAccess =
                            permission === Permission.editDepots();
                        const hasPermissionToOpenAllDoors =
                            permission === Permission.openDepotDoors(depot.id);
                        // eslint-disable-next-line max-len
                        const hasPermisstionToOpenSpecificDoor =
                            permission.indexOf(
                                Permission.openDepotDoors(depot.id) + ':',
                            ) !== -1;
                        return (
                            hasFullAccess ||
                            hasPermissionToOpenAllDoors ||
                            hasPermisstionToOpenSpecificDoor
                        );
                    },
                ),
            );
        },
    );
};

export const makeReadPermittedDepotListSelector = () => {
    return createSelector(
        [depotListSelector, makePermissionListSelector()],
        (depotsList, permissionsList): Depot[] => {
            return permissionsList.includes(Permission.readDepots())
                ? depotsList
                : [];
        },
    );
};

export const makeStatsPermittedDepotListSelector = () => {
    return createSelector(
        [depotListSelector, makePermissionListSelector()],
        (depotsList, permissionsList): Depot[] => {
            if (
                permissionsList.includes(Permission.accessDepotsBookingStats())
            ) {
                return depotsList;
            }

            return depotsList.filter((depot) =>
                permissionsList.includes(
                    Permission.accessDepotBookingStats(depot.id),
                ),
            );
        },
    );
};

export const makeDoorStatusSelector = (depotId: string) => {
    return createSelector(
        [doorStatusListSelector],
        (doorStatusList): DoorStatus | undefined => {
            return doorStatusList[depotId];
        },
    );
};

export const makeDoorStatusLoadingSelector = (depotId: string) => {
    return createSelector(
        [doorStatusLoadingListSelector],
        (doorStatusLoadingList): boolean => {
            return doorStatusLoadingList?.[depotId] ?? false;
        },
    );
};
